import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Analytic accounts: income, costs and the result per shop, department, project or vehicle, from
 * the same books. Rules share lines out as they are posted; the rest can be shared out by hand.
 */

interface Analytic {
  id: string;
  code: string;
  name: string;
  plan: string;
  active: boolean;
}
interface Rule {
  id: string;
  analytic: string;
  share: number;
  account: string | null;
  account_code: string | null;
  account_name: string | null;
  contains: string | null;
  journal: string | null;
  active: boolean;
}
interface Setup {
  analytics: Analytic[];
  rules: Rule[];
  plans: string[];
}
interface ReportRow extends Analytic {
  income: number;
  costs: number;
  result: number;
  lines: number;
}
interface Report {
  analytics: ReportRow[];
  unshared: { income: number; costs: number; result: number; lines: number };
}
interface LineRow {
  id: string;
  entry: string;
  number: number;
  date: string;
  memo: string;
  account_code: string;
  account_name: string;
  label: string | null;
  partner: string | null;
  amount: number;
  share_amount: number;
  share: number;
  shares: { analytic: string; share: number }[];
}
interface ChartAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  active?: boolean;
}

const FULL = 10_000;
const pct = (share: number) => `${(share / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} %`;
const parsePct = (s: string) => {
  const n = Number(s.replace(',', '.').replace('%', '').trim());
  return Number.isFinite(n) && n > 0 && n <= 100 ? Math.round(n * 100) : null;
};

export function AnalyticTab({ from, to, version, accounts, onOpenEntry }: { from: string; to: string; version: number; accounts: ChartAccount[]; onOpenEntry: (id: string) => void }) {
  const { can } = useSession();
  const settings = can('accounting.settings');
  const [v, setV] = useState(0);
  const [chosen, setPlan] = useState<string | null>(null);
  const { data: setup, reload: reloadSetup } = useAction<Setup>('accounting.analytics', { v: version + v });
  // One plan at a time: a line can be shared out once per plan, so plans do not add up.
  const plan = chosen && setup?.plans.includes(chosen) ? chosen : (setup?.plans[0] ?? null);
  const { data } = useAction<Report>('accounting.analytic_report', { from, to, plan, v: version + v });
  const [dialog, setDialog] = useState<'accounts' | 'rules' | null>(null);
  const [lines, setLines] = useState<ReportRow | 'unshared' | null>(null);
  const { busy, run } = useSave();
  const changed = () => {
    setV((x) => x + 1);
    void reloadSetup();
  };
  const apply = () =>
    void run(async () => {
      const r = await action<{ checked: number; tagged: number }>('accounting.apply_analytic_rules', { from, to });
      changed();
      return r;
    }, t('anl.applied'));

  if (!setup || !data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const total = data.analytics.reduce((s, a) => ({ income: s.income + a.income, costs: s.costs + a.costs }), { income: 0, costs: 0 });

  return (
    <div className="rep-grid">
      <section className="panel rep-wide">
        <div className="panel-head">
          <div>
            <h2>{t('anl.title')}</h2>
            <p className="muted small">{t('anl.subtitle')}</p>
          </div>
          {settings ? (
            <span className="foot-actions">
              <Button variant="secondary" icon="layers" onClick={() => setDialog('accounts')}>
                {t('anl.accounts')}
              </Button>
              {setup.analytics.length ? (
                <Button variant="secondary" icon="sliders" onClick={() => setDialog('rules')}>
                  {tn('anl.rules_n', setup.rules.length)}
                </Button>
              ) : null}
            </span>
          ) : null}
        </div>
        {!setup.analytics.length ? (
          <EmptyState
            icon="layers"
            title={t('anl.none')}
            body={t('anl.none_body')}
            action={
              settings ? (
                <Button variant="primary" icon="plus" onClick={() => setDialog('accounts')}>
                  {t('anl.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {setup.plans.length > 1 ? (
              <div className="segmented" role="radiogroup" aria-label={t('anl.plan')}>
                {setup.plans.map((p) => (
                  <button key={p} type="button" role="radio" aria-checked={plan === p} className={plan === p ? 'active' : ''} onClick={() => setPlan(p)}>
                    {p}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="rep-kpis anl-kpis">
              <Kpi label={t('anl.k_income')} value={money(total.income)} />
              <Kpi label={t('anl.k_costs')} value={money(total.costs)} />
              <Kpi label={t('anl.k_result')} value={money(total.income - total.costs)} />
              <Kpi label={t('anl.k_unshared')} value={String(data.unshared.lines)} hint={t('anl.unshared_hint', { income: money(data.unshared.income), costs: money(data.unshared.costs) })} />
            </div>
            <Table
              view="accounting.analytic"
              head={[t('anl.c_code'), t('anl.c_name'), t('anl.k_income'), t('anl.k_costs'), t('anl.k_result')]}
              phoneHide={[0]}
              left={[1]}
              rows={[
                ...data.analytics.map((a) => ({
                  key: a.id,
                  onClick: () => setLines(a),
                  cells: [
                    <span className="tabular">{a.code}</span>,
                    <button type="button" className="link-button" onClick={() => setLines(a)}>
                      {a.name}
                    </button>,
                    <span className="tabular">{money(a.income)}</span>,
                    <span className="tabular">{money(a.costs)}</span>,
                    <strong className={`tabular ${a.result < 0 ? 'text-danger' : ''}`}>{money(a.result)}</strong>,
                  ],
                })),
                {
                  key: 'unshared',
                  onClick: () => setLines('unshared'),
                  cells: [
                    '',
                    <button type="button" className="link-button muted" onClick={() => setLines('unshared')}>
                      {t('anl.unshared')}
                    </button>,
                    <span className="tabular muted">{money(data.unshared.income)}</span>,
                    <span className="tabular muted">{money(data.unshared.costs)}</span>,
                    <span className="tabular muted">{money(data.unshared.result)}</span>,
                  ],
                },
              ]}
            />
            {settings && setup.rules.length ? (
              <p className="muted small anl-apply">
                {t('anl.apply_hint')}{' '}
                <Button size="sm" variant="ghost" icon="repeat" busy={busy} onClick={apply}>
                  {t('anl.apply')}
                </Button>
              </p>
            ) : null}
          </>
        )}
      </section>
      {dialog === 'accounts' ? <AccountsDialog setup={setup} onClose={() => setDialog(null)} onChanged={changed} /> : null}
      {dialog === 'rules' ? <RulesDialog setup={setup} accounts={accounts} onClose={() => setDialog(null)} onChanged={changed} /> : null}
      {lines ? (
        <LinesDialog
          from={from}
          to={to}
          plan={plan}
          analytic={lines === 'unshared' ? null : lines}
          setup={setup}
          canShare={can('accounting.post')}
          onClose={() => setLines(null)}
          onOpenEntry={onOpenEntry}
          onChanged={changed}
        />
      ) : null}
    </div>
  );
}

function AccountsDialog({ setup, onClose, onChanged }: { setup: Setup; onClose: () => void; onChanged: () => void }) {
  const [editing, setEditing] = useState<Analytic | 'new' | null>(setup.analytics.length ? null : 'new');
  const [form, setForm] = useState({ code: '', name: '', plan: setup.plans[0] ?? '', active: true });
  const { busy, run } = useSave();
  const edit = (a: Analytic | 'new') => {
    setEditing(a);
    setForm(a === 'new' ? { code: '', name: '', plan: setup.plans[0] ?? '', active: true } : { code: a.code, name: a.name, plan: a.plan, active: a.active });
  };
  const back = () => (setup.analytics.length ? setEditing(null) : onClose());
  const save = () =>
    void run(async () => {
      await action('accounting.save_analytic', { ...(editing && editing !== 'new' ? { id: editing.id } : {}), ...form });
      onChanged();
      setEditing(null);
    }, t('anl.saved', { name: form.name.trim() }));

  if (editing) {
    return (
      <Dialog
        title={editing === 'new' ? t('anl.add') : editing.name}
        onClose={back}
        footer={
          <>
            <Button variant="ghost" onClick={back}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!form.code.trim() || form.name.trim().length < 2 || form.plan.trim().length < 2} onClick={save}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Field label={t('anl.c_name')} required>
            <input className="input" value={form.name} maxLength={80} placeholder={t('anl.name_ph')} onChange={(e) => setForm({ ...form, name: e.target.value })} data-autofocus />
          </Field>
          <Field label={t('anl.c_code')} required>
            <input className="input tabular" value={form.code} maxLength={20} autoCapitalize="characters" onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label={t('anl.plan')} required hint={t('anl.plan_hint')}>
            <input className="input" list="anl-plans" value={form.plan} maxLength={40} onChange={(e) => setForm({ ...form, plan: e.target.value })} />
            <datalist id="anl-plans">
              {setup.plans.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </Field>
          {editing !== 'new' ? (
            <label className="check-row">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span>
                <span className="strong">{t('anl.in_use')}</span>
                <span className="muted">{t('anl.in_use_hint')}</span>
              </span>
            </label>
          ) : null}
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t('anl.accounts')}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="plus" onClick={() => edit('new')}>
          {t('anl.add')}
        </Button>
      }
    >
      <ul className="bank-acc-list">
        {setup.analytics.map((a) => (
          <li key={a.id}>
            <button type="button" className="bank-acc-row" onClick={() => edit(a)}>
              <Icon name="layers" size={18} />
              <span>
                <strong>{a.name}</strong>
                <span className="muted small tabular">
                  {a.code} · {a.plan}
                </span>
              </span>
              {!a.active ? <StatusBadge tone="neutral" icon="pause" label={t('anl.not_in_use')} /> : null}
              <Icon name="edit" size={16} className="muted" />
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

function RulesDialog({ setup, accounts, onClose, onChanged }: { setup: Setup; accounts: ChartAccount[]; onClose: () => void; onChanged: () => void }) {
  const active = setup.analytics.filter((a) => a.active);
  const blank = { analytic: active[0]?.id ?? '', share: '100', account: '', contains: '' };
  const [editing, setEditing] = useState<Rule | 'new' | null>(setup.rules.length ? null : 'new');
  const [form, setForm] = useState(blank);
  const [removing, setRemoving] = useState<Rule | null>(null);
  const { busy, run } = useSave();
  const name = (id: string) => setup.analytics.find((a) => a.id === id)?.name ?? '';
  const pl = accounts.filter((a) => a.type === 'income' || a.type === 'expense');
  const share = parsePct(form.share);
  const edit = (r: Rule | 'new') => {
    setEditing(r);
    setForm(r === 'new' ? blank : { analytic: r.analytic, share: String(r.share / 100), account: r.account ?? '', contains: r.contains ?? '' });
  };
  const back = () => (setup.rules.length ? setEditing(null) : onClose());
  const save = () =>
    void run(async () => {
      await action('accounting.save_analytic_rule', {
        ...(editing && editing !== 'new' ? { id: editing.id } : {}),
        analytic: form.analytic,
        share,
        account: form.account || null,
        contains: form.contains.trim() || null,
      });
      onChanged();
      setEditing(null);
    }, t('anl.rule_saved'));
  const what = (r: Rule) => [r.account_code ? `${r.account_code} ${r.account_name}` : null, r.contains ? t('anl.rule_words', { words: r.contains }) : null].filter(Boolean).join(' · ');

  if (editing) {
    return (
      <Dialog
        title={editing === 'new' ? t('anl.add_rule') : t('anl.edit_rule')}
        onClose={back}
        footer={
          <>
            <Button variant="ghost" onClick={back}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!form.analytic || share === null || (!form.account && !form.contains.trim())} onClick={save}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          <p className="muted small">{t('anl.rule_intro')}</p>
          <div className="form-grid">
            <Field label={t('anl.rule_account')} hint={t('anl.rule_account_hint')}>
              <select className="input" value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })}>
                <option value="">{t('anl.any_account')}</option>
                {pl.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('anl.rule_contains')} hint={t('anl.rule_contains_hint')}>
              <input className="input" value={form.contains} maxLength={200} placeholder={t('anl.rule_contains_ph')} onChange={(e) => setForm({ ...form, contains: e.target.value })} />
            </Field>
            <Field label={t('anl.rule_to')} required>
              <select className="input" value={form.analytic} onChange={(e) => setForm({ ...form, analytic: e.target.value })}>
                {active.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.plan}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('anl.share')} hint={t('anl.share_hint')} error={share === null ? t('anl.share_rule') : null}>
              <input className="input tabular" inputMode="decimal" value={form.share} onChange={(e) => setForm({ ...form, share: e.target.value })} />
            </Field>
          </div>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t('anl.rules')}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="primary" icon="plus" onClick={() => edit('new')}>
          {t('anl.add_rule')}
        </Button>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('anl.rules_order')}</p>
        <Table
          head={[t('anl.rule_lines'), t('anl.rule_to'), t('anl.share'), '']}
          left={[1]}
          sortable={false}
          caption={t('anl.rules')}
          rows={setup.rules.map((r) => ({
            key: r.id,
            onClick: () => edit(r),
            cells: [
              <button type="button" className="link-button" onClick={() => edit(r)}>
                {what(r)}
              </button>,
              name(r.analytic),
              <span className="tabular">{pct(r.share)}</span>,
              <IconButton icon="trash" label={t('anl.remove_rule')} onClick={() => setRemoving(r)} />,
            ],
          }))}
        />
      </div>
      {removing ? (
        <Confirm
          title={t('anl.remove_rule')}
          body={t('anl.remove_rule_body')}
          confirm={t('anl.remove_rule')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('accounting.delete_analytic_rule', { id: removing.id });
              setRemoving(null);
              onChanged();
            }, t('anl.rule_removed'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function LinesDialog({
  from,
  to,
  plan,
  analytic,
  setup,
  canShare,
  onClose,
  onOpenEntry,
  onChanged,
}: {
  from: string;
  to: string;
  plan: string | null;
  analytic: ReportRow | null;
  setup: Setup;
  canShare: boolean;
  onClose: () => void;
  onOpenEntry: (id: string) => void;
  onChanged: () => void;
}) {
  const [v, setV] = useState(0);
  const { data } = useAction<LineRow[]>('accounting.analytic_lines', { from, to, analytic: analytic?.id ?? null, plan, v });
  const [sharing, setSharing] = useState<LineRow | null>(null);
  return (
    <Dialog title={analytic ? `${analytic.name} · ${analytic.plan}` : t('anl.unshared')} onClose={onClose} size="lg">
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !data.length ? (
        <EmptyState icon="checkCircle" title={analytic ? t('anl.no_lines') : t('anl.all_shared')} body={analytic ? t('anl.no_lines_body') : t('anl.all_shared_body')} />
      ) : (
        <Table
          head={[t('anl.c_date'), t('anl.c_entry'), t('anl.c_account'), t('anl.c_what'), analytic ? t('anl.c_share') : t('anl.c_amount'), '']}
          phoneHide={[1, 2]}
          left={[2, 3]}
          caption={t('anl.lines')}
          rows={data.map((l) => ({
            key: l.id,
            sort: [l.date, l.number, l.account_code, l.label ?? l.memo, analytic ? l.share_amount : l.amount, null],
            cells: [
              <span className="tabular">{day(l.date)}</span>,
              <button type="button" className="link-button tabular" onClick={() => onOpenEntry(l.entry)}>
                {l.number}
              </button>,
              <span className="small">
                {l.account_code} {l.account_name}
              </span>,
              <span className="small">{[l.label ?? l.memo, l.partner].filter(Boolean).join(' · ')}</span>,
              <span className="tabular">
                {money(analytic ? l.share_amount : l.amount)}
                {l.share && l.share < FULL ? <span className="muted small"> · {pct(l.share)}</span> : null}
              </span>,
              canShare ? <IconButton icon="split" label={t('anl.share_line')} onClick={() => setSharing(l)} /> : null,
            ],
          }))}
        />
      )}
      {sharing && plan ? (
        <ShareDialog
          line={sharing}
          plan={plan}
          setup={setup}
          onClose={() => setSharing(null)}
          onDone={() => {
            setSharing(null);
            setV((x) => x + 1);
            onChanged();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function ShareDialog({ line, plan, setup, onClose, onDone }: { line: LineRow; plan: string; setup: Setup; onClose: () => void; onDone: () => void }) {
  // Only this plan's accounts: the line's shares in other plans stay as they are.
  const inPlan = setup.analytics.filter((a) => a.plan === plan);
  const mine = line.shares.filter((s) => inPlan.some((a) => a.id === s.analytic));
  const active = inPlan.filter((a) => a.active || mine.some((s) => s.analytic === a.id));
  const [rows, setRows] = useState<{ analytic: string; share: string }[]>(
    mine.length ? mine.map((s) => ({ analytic: s.analytic, share: String(s.share / 100) })) : [{ analytic: active[0]?.id ?? '', share: '100' }],
  );
  const { busy, run } = useSave();
  const shares = rows.map((r) => ({ analytic: r.analytic, share: parsePct(r.share) }));
  const total = shares.reduce((s, x) => s + (x.share ?? 0), 0);
  const ok = shares.every((s) => s.analytic && s.share !== null) && total <= FULL && new Set(shares.map((s) => s.analytic)).size === shares.length;
  const save = () =>
    void run(async () => {
      await action('accounting.share_line', { line: line.id, plan, shares });
      onDone();
    }, t('anl.shared'));
  return (
    <Dialog
      title={t('anl.share_in', { plan })}
      onClose={onClose}
      footer={
        <>
          <span className={`muted small pay-sum ${total > FULL ? 'text-danger' : ''}`} aria-live="polite">
            {t('anl.share_total', { share: pct(total), amount: money(Math.round((line.amount * total) / FULL)) })}
          </span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">
          {line.account_code} {line.account_name} · {line.label ?? line.memo} · <strong className="tabular">{money(line.amount)}</strong>
        </p>
        {rows.map((r, i) => (
          <div key={i} className="anl-share-row">
            <Field label={t('anl.rule_to')}>
              <select className="input" value={r.analytic} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, analytic: e.target.value } : x)))}>
                {active.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('anl.share')} error={parsePct(r.share) === null ? t('anl.share_rule') : null}>
              <input className="input tabular" inputMode="decimal" value={r.share} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} />
            </Field>
            <IconButton icon="trash" label={t('anl.remove_share')} onClick={() => setRows(rows.filter((_, j) => j !== i))} />
          </div>
        ))}
        <div>
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setRows([...rows, { analytic: active.find((a) => !rows.some((r) => r.analytic === a.id))?.id ?? '', share: String(Math.max(0, (FULL - total) / 100)) }])}>
            {t('anl.add_share')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
